// Preview-mode profiles let the same synthetic-data machinery serve
// different vertical narratives. Every preview surface reads the active
// profile and dispatches on it — the module-level storage key persists the
// selection across reloads, alongside the existing `previewMode` flag.

export type PreviewProfileKey = "healthcare" | "pharma";

export const DEFAULT_PREVIEW_PROFILE: PreviewProfileKey = "healthcare";

export const PREVIEW_PROFILES: readonly {
  key: PreviewProfileKey;
  label: string;
  description: string;
}[] = [
  {
    key: "healthcare",
    label: "Healthcare",
    description: "Meridian Health System — the default preview dataset.",
  },
  {
    key: "pharma",
    label: "Pharma",
    description: "Zephyra brand on Verenta, with Calderis fulfillment.",
  },
];

const PREVIEW_PROFILE_STORAGE_KEY = "previewModeProfile";

let cachedProfile: PreviewProfileKey | null = null;

function isValidProfileKey(value: unknown): value is PreviewProfileKey {
  return value === "healthcare" || value === "pharma";
}

export function getPreviewProfile(): PreviewProfileKey {
  if (cachedProfile !== null) return cachedProfile;
  try {
    const raw = localStorage.getItem(PREVIEW_PROFILE_STORAGE_KEY);
    cachedProfile = isValidProfileKey(raw) ? raw : DEFAULT_PREVIEW_PROFILE;
  } catch {
    cachedProfile = DEFAULT_PREVIEW_PROFILE;
  }
  return cachedProfile;
}

export function getPreviewProfileLabel(key: PreviewProfileKey): string {
  const match = PREVIEW_PROFILES.find((p) => p.key === key);
  return match?.label ?? key;
}

// Persists the chosen profile and reloads so module-level caches (the
// runtime flag, the synthetic graph, the pharma dataset) rebuild against
// the new selection. Mirrors enablePreviewMode()'s reload-on-write pattern.
export function setPreviewProfileAndReload(profile: PreviewProfileKey): void {
  try {
    localStorage.setItem(PREVIEW_PROFILE_STORAGE_KEY, profile);
  } catch {
    // localStorage may be unavailable; fall back to a session-only cache
    // and still reload so the runtime picks up the default.
  }
  cachedProfile = null;
  window.location.reload();
}

export function clearPreviewProfileFlag(): void {
  try {
    localStorage.removeItem(PREVIEW_PROFILE_STORAGE_KEY);
  } catch {
    // ignored — cache reset below still wins for the rest of the session.
  }
  cachedProfile = null;
}

export function __resetPreviewProfileCache(): void {
  cachedProfile = null;
}
